import cx from "classnames";
import { format } from "date-fns";
import { fr } from "date-fns/locale";
import * as React from "react";
import { BiCheckDouble } from "react-icons/bi";
import { FaWhatsapp } from "react-icons/fa";
import { useDispatch, useSelector } from "react-redux";
import { GetWhatsappConversations } from "../../../actionTypes/whatsapp";
import { ModalWrapper } from "../../../components/ModalWrapper";
import { Tooltip } from "../../../components/Tooltip";
import { useApiSelector } from "../../../components/Utils/hook";
import { Application } from "../../../interfaces/resources/application";
import { Talent } from "../../../interfaces/resources/talent";
import { WhatsappMessage, WhatsappConversation } from "../../../interfaces/resources/whatsapp";
import { State } from "../../../interfaces/state";
import {
  clearWhatsappConversationsAction,
  // fetchConversationSummaryAction,
  getWhatsappConversationsAction,
} from "../../../services/api/whatsapp/actions";
import globalStyles from "../../../styles/global.module.scss";
import styles from "./index.module.scss";

interface Props {
  onCloseModal: () => void;
  application: Application;
}

interface CtaProps {
  application: Application;
}

export const WhatsappModalCta: React.FC<CtaProps> = (props: CtaProps) => {
  const { application } = props;
  const [openModal, setOpenModal] = React.useState(false);

  const closeModal = () => {
    setOpenModal(false);
  };

  return (
    <>
      <Tooltip
        content={
          <div className={styles.tooltipContent}>
            <div>Historique Whatsapp</div>
          </div>
        }
      >
        <button
          type="button"
          className={cx(globalStyles.cta, styles.cta, styles.whatsappCta)}
          onClick={() => {
            setOpenModal(true);
          }}
        >
          <FaWhatsapp />
        </button>
      </Tooltip>
      <ModalWrapper open={openModal} onCloseModal={closeModal}>
        <WhatsappModal onCloseModal={closeModal} application={application} />
      </ModalWrapper>
    </>
  );
};

export const WhatsappModal: React.FC<Props> = (props: Props) => {
  const { onCloseModal, application: applicationInit } = props;
  const dispatch = useDispatch();

  const applicationState = useSelector((state: State) => state.application);
  const application = applicationState?.["@id"] === applicationInit["@id"] ? applicationState : applicationInit;
  const currentUser = useSelector((state: State) => state.currentUser);
  const { apiPendingRequests } = useApiSelector();
  // const prevApiErrors = usePrevious(apiErrors);
  // const prevApiSuccess = usePrevious(apiSuccess);

  // const resetFormRef = React.useRef<any>(null);
  // const [isFormSubmitting, setIsFormSubmitting] = React.useState(false);

  // All conversations for the Talent
  const whatsappConversations = useSelector((state: State) => state.whatsappConversations);

  // Which conversation is currently selected in the left panel?
  const [selectedConversationId, setSelectedConversationId] = React.useState<string | null>(null);

  // Whether we’re displaying the “summary” view instead of a single conversation
  const [showSummary, setShowSummary] = React.useState(false);

  // State to hold the summary text (once we get it from OpenAI)
  const [loadingSummary] = React.useState(false);
  const [summaryText] = React.useState<string | null>(null);
  const [summaryError] = React.useState<string | null>(null);

  // const validationSchema = Yup.object().shape({
  //   message: Yup.string().required(),
  // });

  // Utility to close the modal
  const closeModal = () => {
    if (typeof onCloseModal === "function") {
      onCloseModal();
      clearWhatsappConversationsAction();
    }
  };

  // Helper to compare phone numbers
  function normalizePhoneNumber(input: string): string {
    const [phonePart] = input.split("@");
    return phonePart.replace(/\D/g, "");
  }

  function arePhonesEqual(p1: string | undefined, p2: string | undefined): boolean {
    if (!p1 || !p2) {
      return false;
    }
    return normalizePhoneNumber(p1) === normalizePhoneNumber(p2);
  }

  // Fetch all whatsapp conversations for the Talent
  const getWhatsappConversations = (talent: Talent) => {
    dispatch(getWhatsappConversationsAction(talent["@id"]));
  };

  // const fetchConversationsSummary = () => {
  //   if (!whatsappConversations) return;
  //   setLoadingSummary(true);
  //   setSummaryError(null);
  //   setSummaryText(null);

  //   dispatch(fetchConversationSummaryAction(application.talent["@id"]));
  // };

  // Toggle summary mode on
  // const handleShowSummary = () => {
  //   setShowSummary(true);
  //   setSelectedConversationId(null); // Unselect any conversation
  //   // Fetch summary if not already done
  //   if (!summaryText) {
  //     fetchConversationsSummary();
  //   }
  // };

  // Toggle summary mode off
  // const handleCloseSummary = () => {
  //   setShowSummary(false);
  //   setSummaryError(null);
  // };

  // const onSubmit = (values: FormikValues, formikHelpers: FormikHelpers<{ message: string }>) => {
  //   setIsFormSubmitting(true);
  //   resetFormRef.current = formikHelpers.resetForm;
  //   // TODO: dispatch your "send message" action here
  // };

  // Listen for successful post to reset form
  // React.useEffect(() => {
  //   const apiSuccessIntersection = [...apiSuccess].slice(prevApiSuccess?.length);
  //   if (apiSuccessIntersection.some((action) => [PostApplicationMessage.SUCCESS].includes(action.type))) {
  //     dispatch(getOfferApplicationAction(application["@id"]));
  //     if (typeof resetFormRef.current === "function") {
  //       resetFormRef.current({ message: "" });
  //     }
  //     setIsFormSubmitting(false);
  //   }
  // }, [apiErrors, apiSuccess, prevApiErrors, prevApiSuccess, dispatch, resetFormRef, application]);

  // On mount, load conversations
  React.useEffect(() => {
    if (
      application.talent
      && (!whatsappConversations || whatsappConversations.length === 0)
      && !apiPendingRequests.some((request) => request.type === GetWhatsappConversations.REQUEST)
    ) {
      getWhatsappConversations(application.talent);
    }
  }, [whatsappConversations, application.talent, apiPendingRequests]);

  // Group messages by date
  function groupMessagesByDate(messages: WhatsappMessage[]): Record<string, WhatsappMessage[]> {
    return messages.reduce((acc, msg) => {
      const dateKey = format(new Date(msg.sentAt), "yyyy-MM-dd");
      if (!acc[dateKey]) {
        acc[dateKey] = [];
      }
      acc[dateKey].push(msg);
      return acc;
    }, {} as Record<string, WhatsappMessage[]>);
  }

  // The conversation the user has clicked in the left panel
  const selectedConversation: WhatsappConversation | undefined = React.useMemo(() => {
    if (!whatsappConversations) return undefined;
    return whatsappConversations.find((c) => c["@id"] === selectedConversationId);
  }, [whatsappConversations, selectedConversationId]);

  return (
    <div className={styles.modal}>
      <div className={styles.container}>
        <div className={styles.titleContainer}>
          <h3 className={styles.title}>Historique Whatsapp</h3>
        </div>
        <div className={styles.mainContent}>
          <div className={styles.leftPanel}>
            <div style={{ marginBottom: "10px" }}>
              {/* {showSummary ? (
                <button
                  type="button"
                  className={cx(globalStyles.cta, globalStyles.darkCta)}
                  onClick={handleCloseSummary}
                >
                  Revenir
                </button>
              ) : (
                <button
                  type="button"
                  className={cx(globalStyles.cta, globalStyles.primaryCta)}
                  onClick={handleShowSummary}
                >
                  Synthèse
                </button>
              )} */}
            </div>

            {/* Only display conversation list if not in Summary view */}
            {!showSummary && (
              <>
                <h4>Conversations</h4>
                {whatsappConversations && whatsappConversations.length > 0 ? (
                  whatsappConversations.map((conversation) => (
                    <div
                      key={conversation["@id"]}
                      onClick={() => {
                        setSelectedConversationId(conversation["@id"]);
                        setShowSummary(false);
                      }}
                      className={cx(styles.conversationItem, {
                        [styles.active]: conversation["@id"] === selectedConversationId,
                      })}
                    >
                      {conversation.coreUser.firstname} - {conversation.talent.firstname}
                    </div>
                  ))
                ) : (
                  <div>Aucune conversation trouvée.</div>
                )}
              </>
            )}
          </div>
          {/* RIGHT PANEL — messages of the selected conversation or summary */}
          <div className={styles.rightPanel}>
            {showSummary ? (
              // SUMMARY MODE
              <div className={styles.messagesContainer}>
                {loadingSummary && <div>Chargement de la synthèse...</div>}
                {summaryError && (
                  <div style={{ color: "red" }}>Erreur lors de la génération de la synthèse: {summaryError}</div>
                )}
                {!loadingSummary && !summaryError && summaryText && (
                  <div>
                    <h4>Résumé des conversations</h4>
                    <p>{summaryText}</p>
                  </div>
                )}
              </div>
            ) : selectedConversation ? (
              // SELECTED CONVERSATION
              <div className={styles.messagesContainer}>
                {currentUser
                  && selectedConversation.whatsappMessages
                  && (() => {
                    const groupedMessages = groupMessagesByDate(selectedConversation.whatsappMessages);
                    return Object.entries(groupedMessages).map(([dateKey, dailyMessages]) => {
                      const dayLabel = format(new Date(dateKey), "dd/MM/yyyy", { locale: fr });
                      return (
                        <div key={dateKey} className={styles.newsFeedListContainer}>
                          <div className={cx(styles.newsFeedListItemContent)}>
                            <div className={styles.whatsappDateContainer}>
                              <div className={styles.dateHeader}>{dayLabel}</div>
                              {dailyMessages.map((message) => {
                                const senderIsCurrentUser = arePhonesEqual(message.sender, currentUser.phone);
                                return (
                                  <div key={message["@id"]} className={styles.whatsappMargin}>
                                    <div
                                      className={cx(styles.whatsappPadding, {
                                        [styles.newsFeedListItemTitleTextSender]: senderIsCurrentUser,
                                      })}
                                    >
                                      <div
                                        className={cx(styles.whatsappMessageContainer, {
                                          [styles.firstMessage]: message.message.includes(
                                            "Messages are end-to-end encrypted",
                                          ),
                                        })}
                                      >
                                        <span>{message.message}</span>
                                        <div className={styles.messageTime}>
                                          {format(new Date(message.sentAt), "HH:mm")}
                                          {/* Show read/unread checks if currentUser is the sender */}
                                          {senderIsCurrentUser
                                            && (message.seen ? (
                                              <BiCheckDouble className={styles.messageRead} />
                                            ) : (
                                              <BiCheckDouble className={styles.messageUnRead} />
                                            ))}
                                        </div>
                                      </div>
                                    </div>
                                  </div>
                                );
                              })}
                            </div>
                          </div>
                        </div>
                      );
                    });
                  })()}
                {/* The form to send a new message (currently commented out) */}
                {/* <Formik onSubmit={onSubmit} initialValues={{ message: "" }} validationSchema={validationSchema}>
              {() => (
                <Form>
                  <div className={styles.messageFormContent}>
                    <div>
                      <Field
                        component={RichTextEditor}
                        uniqId={`message-modal-app-${application["@id"]}`}
                        required={true}
                        replaceIfEmpty={false}
                        name="message"
                        className={styles.messageTextarea}
                        placeholder="Écrivez un message..."
                        label="Envoyer un whatsapp"
                        inlineSubmitTrigger={
                          <div>
                            <button
                              type="submit"
                              disabled={isFormSubmitting}
                              className={cx(globalStyles.cta, globalStyles.aquaCta)}
                            >
                              Envoyer
                            </button>
                          </div>
                        }
                      />
                    </div>
                  </div>
                </Form>
              )}
            </Formik> */}
              </div>
            ) : (
              // NO CONVERSATION SELECTED
              <div className={styles.noConversationSelected}>Sélectionnez une conversation à gauche</div>
            )}
          </div>
        </div>
        <div className={styles.ctaListContainer}>
          <div className={styles.ctaCloseContainer}>
            <button
              type="button"
              onClick={closeModal}
              className={cx(globalStyles.cta, globalStyles.darkCta, globalStyles.inverted)}
            >
              Fermer
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};
